import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { ArrowLeft, ArrowRight, Check, Search, Loader2, X, Mail, MessageSquare } from 'lucide-react';
import { supabase } from '../../supabase';
import {
  ADD_ONS, ONE_TIME_PACKAGES, MAINTENANCE_PRICING, RUSH_FEE,
  carMakes, carModels, years, detectVehicleType,
} from '../../data/constants';

// ── Pricing ────────────────────────────────────────────────────────────────
const VEHICLE_TYPES = [
  { id: 'sedan', label: 'Sedan / Coupe',          desc: 'Cars, coupes, compacts', icon: '🚗' },
  { id: 'suv',   label: 'SUV / Crossover',         desc: 'Midsize & full-size SUVs', icon: '🚙' },
  { id: 'truck', label: 'Truck / Full-Size / Van', desc: 'Pickups, large SUVs, minivans, work vans', icon: '🛻' },
];

// Services and prices come from the same catalog as the public booking flow
// so a manual booking can never be priced differently from the website.
// Steam + deep shampoo are already included in The Alchemist and the Reset.
const INCLUDED_IN_ALCHEMIST = ['steam_clean', 'carpet_shampoo'];
const SERVICE_OPTS = [
  ...ONE_TIME_PACKAGES.filter((p) => p.id !== 'dev_test').map((p) => ({
    id: p.id, label: p.name, booking_type: 'one_time', emoji: p.emoji, desc: p.tagline,
    prices: { sedan: p.price, suv: p.suvPrice, truck: p.truckPrice },
    includedAddOns: p.id === 'alchemist' ? INCLUDED_IN_ALCHEMIST : [],
  })),
  {
    id: 'maintenance_reset', label: 'Alchemist Reset', booking_type: 'maintenance_reset', emoji: '✨',
    desc: 'Baseline service to start the Maintenance Program.',
    prices: MAINTENANCE_PRICING.reset, includedAddOns: INCLUDED_IN_ALCHEMIST,
  },
];
const servicePrice = (service, vehicleId) => service.prices[vehicleId] ?? service.prices.sedan;
const addOnsFor = (service) => ADD_ONS.filter((a) => !service.includedAddOns.includes(a.id));

const PAYMENT_OPTS = [
  { id: 'none',    label: 'No payment yet',     desc: 'Invoice later' },
  { id: 'deposit', label: 'Deposit collected',  desc: '50% upfront' },
  { id: 'full',    label: 'Paid in full',        desc: 'Balance $0' },
];

const STEPS = ['Client', 'Service', 'Schedule', 'Confirm'];

// ── Step indicator ──────────────────────────────────────────────────────────
function StepBar({ step }) {
  return (
    <div className="flex items-center gap-1 mb-8">
      {STEPS.map((s, i) => (
        <React.Fragment key={s}>
          <div className={`flex items-center gap-1.5 text-xs font-semibold transition-colors ${
            i < step ? 'text-emerald-600' : i === step ? 'text-[#C9A84C]' : 'text-gray-300'
          }`}>
            <span className={`w-6 h-6 rounded-full flex items-center justify-center text-xs border-2 transition-all ${
              i < step ? 'border-emerald-500 bg-emerald-500 text-white' :
              i === step ? 'border-[#C9A84C] text-[#C9A84C]' :
              'border-gray-200 text-gray-300'
            }`}>
              {i < step ? <Check size={12} /> : i + 1}
            </span>
            <span className="hidden sm:block">{s}</span>
          </div>
          {i < STEPS.length - 1 && (
            <div className={`flex-1 h-0.5 mx-1 rounded transition-colors ${i < step ? 'bg-emerald-400' : 'bg-gray-200'}`} />
          )}
        </React.Fragment>
      ))}
    </div>
  );
}

// ── Step 1: Client ──────────────────────────────────────────────────────────
function StepClient({ data, onChange }) {
  const [mode, setMode] = useState(data.clientId ? 'existing' : 'new');
  const [query, setQuery] = useState(data.clientName || '');
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);

  const search = useCallback(async (q) => {
    if (q.length < 2) { setResults([]); return; }
    setSearching(true);
    const { data: rows } = await supabase
      .from('clients')
      .select('id, name, email, phone, year, make, model, color, address')
      .or(`name.ilike.%${q}%,email.ilike.%${q}%,phone.ilike.%${q}%`)
      .limit(8);
    setResults(rows || []);
    setSearching(false);
  }, []);

  useEffect(() => {
    const t = setTimeout(() => search(query), 300);
    return () => clearTimeout(t);
  }, [query, search]);

  const selectClient = (c) => {
    onChange({
      clientId: c.id,
      clientName: c.name,
      email: c.email || '',
      phone: c.phone || '',
      existingVehicle: c.year ? { year: c.year, make: c.make, model: c.model, color: c.color } : null,
      address: c.address || '',
    });
    setQuery(c.name);
    setResults([]);
  };

  const clearClient = () => {
    onChange({ clientId: null, clientName: '', email: '', phone: '', existingVehicle: null });
    setQuery('');
    setResults([]);
  };

  return (
    <div className="space-y-5">
      <div>
        <h2 className="text-lg font-bold text-gray-900 mb-1">Who's the client?</h2>
        <p className="text-sm text-gray-400">Search an existing client or enter new contact info.</p>
      </div>

      <div className="flex gap-2 p-1 bg-gray-100 rounded-xl w-fit">
        {[['existing', 'Search Existing'], ['new', 'New Client']].map(([id, label]) => (
          <button
            key={id}
            onClick={() => { setMode(id); clearClient(); }}
            className={`px-4 py-1.5 rounded-lg text-sm font-medium transition-all ${
              mode === id ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            {label}
          </button>
        ))}
      </div>

      {mode === 'existing' ? (
        <div className="space-y-3">
          <div className="relative">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              placeholder="Search by name, email, or phone…"
              value={query}
              onChange={e => { setQuery(e.target.value); if (data.clientId) clearClient(); }}
              className="w-full pl-9 pr-4 py-2.5 border-2 border-gray-200 rounded-xl text-sm focus:outline-none focus:border-[#C9A84C] text-gray-900 bg-white"
            />
            {searching && <Loader2 size={14} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 animate-spin" />}
          </div>

          {results.length > 0 && !data.clientId && (
            <div className="bg-white border border-gray-200 rounded-xl shadow-sm overflow-hidden">
              {results.map(c => (
                <button
                  key={c.id}
                  onClick={() => selectClient(c)}
                  className="w-full flex items-center gap-3 px-4 py-3 hover:bg-[#C9A84C]/5 border-b border-gray-50 last:border-0 text-left transition-colors"
                >
                  <div className="w-8 h-8 rounded-full bg-[#C9A84C] text-black text-xs font-bold flex items-center justify-center shrink-0">
                    {c.name?.split(' ').map(w => w[0]).join('').toUpperCase().slice(0, 2)}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-semibold text-gray-900 text-sm truncate">{c.name}</p>
                    <p className="text-xs text-gray-400 truncate">{c.email || c.phone}</p>
                  </div>
                  {c.make && <p className="text-xs text-gray-400 shrink-0">{c.year} {c.make}</p>}
                </button>
              ))}
            </div>
          )}

          {data.clientId && (
            <div className="flex items-center gap-3 p-3 bg-emerald-50 border border-emerald-200 rounded-xl">
              <Check size={16} className="text-emerald-600 shrink-0" />
              <div className="flex-1">
                <p className="font-semibold text-emerald-800 text-sm">{data.clientName}</p>
                <p className="text-xs text-emerald-600">{data.email || data.phone}</p>
              </div>
              <button onClick={clearClient} className="text-emerald-400 hover:text-emerald-700"><X size={14} /></button>
            </div>
          )}
        </div>
      ) : (
        <div className="space-y-3">
          {[
            { label: 'Full Name *', field: 'clientName', placeholder: 'e.g. Caprice Williams', type: 'text' },
            { label: 'Email', field: 'email', placeholder: 'client@email.com', type: 'email' },
            { label: 'Phone', field: 'phone', placeholder: '(301) 555-0000', type: 'tel' },
          ].map(({ label, field, placeholder, type }) => (
            <div key={field}>
              <label className="text-xs font-semibold text-gray-500 uppercase tracking-wider block mb-1">{label}</label>
              <input
                type={type}
                value={data[field] || ''}
                onChange={e => onChange({ [field]: e.target.value })}
                placeholder={placeholder}
                className="w-full border-2 border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:border-[#C9A84C] text-gray-900 bg-white"
              />
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ── Step 2: Service & Vehicle ───────────────────────────────────────────────
function StepService({ data, onChange }) {
  const [customMake, setCustomMake] = useState(!carMakes.includes(data.make || ''));
  const [customModel, setCustomModel] = useState(false);

  const selectedService = SERVICE_OPTS.find(s => s.id === data.serviceId) || SERVICE_OPTS[0];
  const selectedVehicle = VEHICLE_TYPES.find(v => v.id === data.vehicleType) || VEHICLE_TYPES[0];
  const addOns = addOnsFor(selectedService);
  const basePrice = servicePrice(selectedService, selectedVehicle.id);

  const toggleAddOn = (id) => {
    const cur = data.addOns || [];
    onChange({ addOns: cur.includes(id) ? cur.filter(x => x !== id) : [...cur, id] });
  };

  const handleMakeChange = (make) => {
    onChange({ make, model: '' });
    setCustomModel(false);
  };

  const handleModelChange = (model) => {
    const vt = detectVehicleType(data.make, model);
    onChange({ model, vehicleType: vt || data.vehicleType || 'sedan' });
  };

  const modelList = data.make && carModels[data.make] ? carModels[data.make] : [];

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-lg font-bold text-gray-900 mb-1">Service & Vehicle</h2>
        <p className="text-sm text-gray-400">Select the service type, then fill in vehicle details.</p>
      </div>

      {/* Service type */}
      <div>
        <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">Service Type</p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {SERVICE_OPTS.map(s => (
            <button
              key={s.id}
              onClick={() => onChange({ serviceId: s.id, addOns: [] })}
              className={`text-left p-4 rounded-xl border-2 transition-colors ${
                data.serviceId === s.id ? 'border-[#C9A84C] bg-[#C9A84C]/5' : 'border-gray-200 hover:border-[#C9A84C]/50'
              }`}
            >
              <span className="text-2xl block mb-1">{s.emoji}</span>
              <p className="font-semibold text-gray-900 text-sm">{s.label}</p>
              <p className="text-xs text-gray-400 mt-0.5">{s.desc}</p>
            </button>
          ))}
        </div>
      </div>

      {/* Vehicle type */}
      <div>
        <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">Vehicle Size</p>
        <div className="grid grid-cols-3 gap-2">
          {VEHICLE_TYPES.map(v => (
            <button
              key={v.id}
              onClick={() => onChange({ vehicleType: v.id })}
              className={`text-center p-3 rounded-xl border-2 transition-colors ${
                data.vehicleType === v.id ? 'border-[#C9A84C] bg-[#C9A84C]/5' : 'border-gray-200 hover:border-[#C9A84C]/50'
              }`}
            >
              <span className="text-xl block mb-1">{v.icon}</span>
              <p className="text-xs font-semibold text-gray-800 leading-tight">{v.label}</p>
              <p className="text-xs text-[#C9A84C] font-bold mt-1">
                ${servicePrice(selectedService, v.id)}
              </p>
            </button>
          ))}
        </div>
        <p className="text-xs text-gray-400 mt-1">Vans, work vans, and commercial vehicles → Truck / Full-Size / Van</p>
      </div>

      {/* Vehicle details */}
      <div>
        <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">Vehicle Details</p>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="text-xs text-gray-400 block mb-1">Year</label>
            <select
              value={data.year || ''}
              onChange={e => onChange({ year: e.target.value })}
              className="w-full border-2 border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:border-[#C9A84C] bg-white text-gray-900"
            >
              <option value="">Year</option>
              {years.map(y => <option key={y} value={y}>{y}</option>)}
            </select>
          </div>
          <div>
            <label className="text-xs text-gray-400 block mb-1">Color</label>
            <input
              type="text"
              value={data.color || ''}
              onChange={e => onChange({ color: e.target.value })}
              placeholder="e.g. White"
              className="w-full border-2 border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:border-[#C9A84C] bg-white text-gray-900"
            />
          </div>
          <div>
            <label className="text-xs text-gray-400 block mb-1">Make</label>
            {customMake ? (
              <div className="flex gap-1">
                <input
                  type="text"
                  value={data.make || ''}
                  onChange={e => onChange({ make: e.target.value, model: '' })}
                  placeholder="e.g. Ford Transit"
                  className="flex-1 border-2 border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:border-[#C9A84C] bg-white text-gray-900"
                />
                <button onClick={() => setCustomMake(false)} className="px-2 text-xs text-gray-400 hover:text-gray-600">↩</button>
              </div>
            ) : (
              <div className="flex gap-1">
                <select
                  value={data.make || ''}
                  onChange={e => handleMakeChange(e.target.value)}
                  className="flex-1 border-2 border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:border-[#C9A84C] bg-white text-gray-900"
                >
                  <option value="">Select make</option>
                  {carMakes.map(m => <option key={m} value={m}>{m}</option>)}
                </select>
                <button onClick={() => setCustomMake(true)} className="px-2 text-xs text-gray-400 hover:text-gray-600 shrink-0" title="Type custom make">✏️</button>
              </div>
            )}
          </div>
          <div>
            <label className="text-xs text-gray-400 block mb-1">Model</label>
            {customModel || !modelList.length ? (
              <div className="flex gap-1">
                <input
                  type="text"
                  value={data.model || ''}
                  onChange={e => handleModelChange(e.target.value)}
                  placeholder="e.g. Transit, Sprinter"
                  className="flex-1 border-2 border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:border-[#C9A84C] bg-white text-gray-900"
                />
                {modelList.length > 0 && <button onClick={() => setCustomModel(false)} className="px-2 text-xs text-gray-400 hover:text-gray-600">↩</button>}
              </div>
            ) : (
              <div className="flex gap-1">
                <select
                  value={data.model || ''}
                  onChange={e => handleModelChange(e.target.value)}
                  className="flex-1 border-2 border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:border-[#C9A84C] bg-white text-gray-900"
                >
                  <option value="">Select model</option>
                  {modelList.map(m => <option key={m} value={m}>{m}</option>)}
                </select>
                <button onClick={() => setCustomModel(true)} className="px-2 text-xs text-gray-400 hover:text-gray-600 shrink-0" title="Type custom model">✏️</button>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Add-ons */}
      <div>
        <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">Add-Ons <span className="font-normal normal-case text-gray-400">(optional)</span></p>
        <div className="grid grid-cols-2 gap-2">
          {addOns.map(a => {
            const on = (data.addOns || []).includes(a.id);
            return (
              <button
                key={a.id}
                onClick={() => toggleAddOn(a.id)}
                className={`text-left p-3 rounded-xl border-2 transition-colors ${
                  on ? 'border-[#C9A84C] bg-[#C9A84C]/10' : 'border-gray-200 hover:border-[#C9A84C]/50'
                }`}
              >
                <span className="text-base block mb-0.5">{a.emoji || a.icon}</span>
                <p className="text-xs font-semibold text-gray-800 leading-tight">{a.label}</p>
                <p className={`text-xs font-bold mt-0.5 ${on ? 'text-[#C9A84C]' : 'text-gray-400'}`}>+${a.price}</p>
              </button>
            );
          })}
        </div>
      </div>

      {/* Price preview */}
      <div className="bg-gray-50 rounded-xl p-3 text-sm">
        <div className="flex justify-between text-gray-600">
          <span>{selectedService.label} — {selectedVehicle.label}</span>
          <span>${basePrice}</span>
        </div>
        {(data.addOns || []).map(id => {
          const a = addOns.find(x => x.id === id);
          return a ? (
            <div key={id} className="flex justify-between text-gray-500 text-xs mt-1">
              <span>{a.label}</span><span>+${a.price}</span>
            </div>
          ) : null;
        })}
        <div className="flex justify-between font-bold text-gray-900 border-t border-gray-200 pt-2 mt-2">
          <span>Estimated Total</span>
          <span className="text-[#C9A84C]">
            ${basePrice + (data.addOns || []).reduce((s, id) => {
              const a = addOns.find(x => x.id === id);
              return s + (a ? a.price : 0);
            }, 0)}
          </span>
        </div>
      </div>
    </div>
  );
}

// ── Step 3: Schedule ────────────────────────────────────────────────────────
function StepSchedule({ data, onChange }) {
  return (
    <div className="space-y-5">
      <div>
        <h2 className="text-lg font-bold text-gray-900 mb-1">Schedule</h2>
        <p className="text-sm text-gray-400">Set the date, time, and service location.</p>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <label className="text-xs font-semibold text-gray-500 uppercase tracking-wider block mb-1">Date *</label>
          <input
            type="date"
            value={data.date || ''}
            onChange={e => onChange({ date: e.target.value })}
            className="w-full border-2 border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:border-[#C9A84C] bg-white text-gray-900"
          />
        </div>
        <div>
          <label className="text-xs font-semibold text-gray-500 uppercase tracking-wider block mb-1">Time</label>
          <input
            type="time"
            value={data.time || '09:00'}
            onChange={e => onChange({ time: e.target.value })}
            className="w-full border-2 border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:border-[#C9A84C] bg-white text-gray-900"
          />
        </div>
      </div>

      <div>
        <label className="text-xs font-semibold text-gray-500 uppercase tracking-wider block mb-1">Service Address *</label>
        <input
          type="text"
          value={data.address || ''}
          onChange={e => onChange({ address: e.target.value })}
          placeholder="Street address where the vehicle will be"
          className="w-full border-2 border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:border-[#C9A84C] bg-white text-gray-900"
        />
        <p className="text-xs text-gray-400 mt-1">Tip: this pre-fills the weather forecast on the job detail page.</p>
      </div>
    </div>
  );
}

// ── Step 4: Confirm ─────────────────────────────────────────────────────────
function StepConfirm({ data, onChange, autoPrice }) {
  const displayPrice = data.priceOverride !== '' && data.priceOverride !== undefined
    ? parseFloat(data.priceOverride) || 0
    : autoPrice;
  const deposit = data.paymentStatus === 'deposit' ? Math.round(displayPrice * 0.5) : data.paymentStatus === 'full' ? displayPrice : 0;

  return (
    <div className="space-y-5">
      <div>
        <h2 className="text-lg font-bold text-gray-900 mb-1">Confirm & Create</h2>
        <p className="text-sm text-gray-400">Review, set price, and log payment.</p>
      </div>

      {/* Summary */}
      <div className="bg-gray-50 rounded-xl p-4 space-y-2 text-sm">
        <div className="flex justify-between"><span className="text-gray-500">Client</span><span className="font-medium text-gray-900">{data.clientName || '—'}</span></div>
        <div className="flex justify-between"><span className="text-gray-500">Service</span><span className="font-medium text-gray-900">{SERVICE_OPTS.find(s => s.id === data.serviceId)?.label || '—'}</span></div>
        <div className="flex justify-between"><span className="text-gray-500">Vehicle</span>
          <span className="font-medium text-gray-900">{[data.year, data.make, data.model].filter(Boolean).join(' ') || '—'}</span>
        </div>
        {(data.addOns || []).length > 0 && (
          <div className="flex justify-between"><span className="text-gray-500">Add-Ons</span>
            <span className="font-medium text-gray-900 text-right text-xs">{(data.addOns || []).join(', ')}</span>
          </div>
        )}
        <div className="flex justify-between"><span className="text-gray-500">Date</span>
          <span className="font-medium text-gray-900">{data.date ? new Date(data.date + 'T12:00').toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' }) : '—'} {data.time || ''}</span>
        </div>
        <div className="flex justify-between"><span className="text-gray-500">Address</span><span className="font-medium text-gray-900 text-right text-xs max-w-xs">{data.address || '—'}</span></div>
      </div>

      {/* Rush */}
      <label className="flex items-start gap-3 p-3 rounded-xl border-2 border-gray-200 hover:border-[#C9A84C]/50 cursor-pointer">
        <input type="checkbox" checked={!!data.rush} onChange={e => onChange({ rush: e.target.checked })} className="mt-0.5 w-4 h-4 accent-[#C9A84C]" />
        <span className="text-sm">
          <span className="font-semibold text-gray-900">Rush / same-day / second car on a booked day (+${RUSH_FEE})</span>
          <span className="block text-xs text-gray-400 mt-0.5">Adds the flat rush fee to the price and notes it on the booking.</span>
        </span>
      </label>

      {/* Price */}
      <div>
        <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">Price</p>
        <div className="flex items-center gap-3">
          <span className="text-sm text-gray-500 shrink-0">Auto: ${autoPrice}</span>
          <div className="relative flex-1">
            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500 font-medium text-sm">$</span>
            <input
              type="number"
              min="0"
              step="1"
              placeholder={`${autoPrice} (override)`}
              value={data.priceOverride !== undefined ? data.priceOverride : ''}
              onChange={e => onChange({ priceOverride: e.target.value })}
              className="w-full pl-7 pr-3 py-2.5 border-2 border-gray-200 rounded-xl text-sm focus:outline-none focus:border-[#C9A84C] bg-white text-gray-900"
            />
          </div>
        </div>
        <p className="text-xs text-gray-400 mt-1">Leave blank to use the auto-calculated price.</p>
      </div>

      {/* Payment status */}
      <div>
        <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">Payment Collected</p>
        <div className="grid grid-cols-3 gap-2">
          {PAYMENT_OPTS.map(p => (
            <button
              key={p.id}
              onClick={() => onChange({ paymentStatus: p.id })}
              className={`p-3 rounded-xl border-2 text-left transition-colors ${
                data.paymentStatus === p.id ? 'border-[#C9A84C] bg-[#C9A84C]/10' : 'border-gray-200 hover:border-[#C9A84C]/50'
              }`}
            >
              <p className="text-xs font-semibold text-gray-800">{p.label}</p>
              <p className="text-xs text-gray-400 mt-0.5">{p.desc}</p>
            </button>
          ))}
        </div>
        {data.paymentStatus === 'deposit' && (
          <p className="text-xs text-[#C9A84C] font-semibold mt-2">Deposit logged: ${deposit} — balance ${displayPrice - deposit} due at service</p>
        )}
        {data.paymentStatus === 'full' && (
          <p className="text-xs text-emerald-600 font-semibold mt-2">Fully paid — no balance due</p>
        )}
      </div>

      {/* Notes */}
      <div>
        <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1">Internal Notes <span className="font-normal normal-case text-gray-400">(optional)</span></p>
        <textarea
          value={data.notes || ''}
          onChange={e => onChange({ notes: e.target.value })}
          rows={3}
          placeholder="E.g. commercial childcare van, park in back, client called to confirm…"
          className="w-full border-2 border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:border-[#C9A84C] bg-white text-gray-900 resize-none"
        />
      </div>

      {/* Notify client */}
      <div>
        <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">Notify Client</p>
        <div className="space-y-2">
          <label className={`flex items-center gap-3 p-3 rounded-xl border-2 cursor-pointer transition-colors ${
            data.notifyEmail ? 'border-[#C9A84C] bg-[#C9A84C]/5' : 'border-gray-200'
          }`}>
            <input
              type="checkbox"
              checked={!!data.notifyEmail}
              onChange={e => onChange({ notifyEmail: e.target.checked })}
              className="w-4 h-4 accent-[#C9A84C]"
            />
            <Mail size={15} className="text-gray-400 shrink-0" />
            <div className="flex-1 min-w-0">
              <p className="text-sm font-semibold text-gray-800">Send confirmation email</p>
              <p className="text-xs text-gray-400 truncate">{data.email ? `→ ${data.email}` : 'No email on file — won\'t send'}</p>
            </div>
          </label>
          <label className="flex items-center gap-3 p-3 rounded-xl border-2 border-gray-100 bg-gray-50 cursor-not-allowed opacity-50 select-none">
            <input type="checkbox" disabled className="w-4 h-4" />
            <MessageSquare size={15} className="text-gray-300 shrink-0" />
            <div>
              <p className="text-sm font-semibold text-gray-400">Send confirmation SMS</p>
              <p className="text-xs text-gray-400">Available once Twilio is set up</p>
            </div>
          </label>
        </div>
      </div>
    </div>
  );
}

// ── Main wizard ─────────────────────────────────────────────────────────────
export default function AdminBookingWizard() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const preClientId = searchParams.get('clientId');

  const [step, setStep] = useState(0);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const [form, setForm] = useState({
    // Step 1 — client
    clientId: null,
    clientName: '',
    email: '',
    phone: '',
    existingVehicle: null,
    // Step 2 — service
    serviceId: 'interior',
    rush: false,
    vehicleType: 'sedan',
    year: '',
    make: '',
    model: '',
    color: '',
    addOns: [],
    // Step 3 — schedule
    date: '',
    time: '09:00',
    address: '',
    // Step 4 — confirm
    priceOverride: '',
    paymentStatus: 'none',
    notes: '',
    notifyEmail: true,
    notifySMS: false,
  });

  // Pre-load client if coming from a profile
  useEffect(() => {
    if (!preClientId) return;
    supabase.from('clients').select('*').eq('id', preClientId).single().then(({ data: c }) => {
      if (!c) return;
      setForm(prev => ({
        ...prev,
        clientId: c.id,
        clientName: c.name || '',
        email: c.email || '',
        phone: c.phone || '',
        address: c.address || '',
        year: c.year || '',
        make: c.make || '',
        model: c.model || '',
        color: c.color || '',
        existingVehicle: c.year ? { year: c.year, make: c.make, model: c.model, color: c.color } : null,
      }));
      // Skip to step 2 if client pre-populated
      setStep(1);
    });
  }, [preClientId]);

  const update = useCallback((patch) => {
    setForm(prev => ({ ...prev, ...patch }));
  }, []);

  // Auto-calculate price
  const service = SERVICE_OPTS.find(s => s.id === form.serviceId) || SERVICE_OPTS[0];
  const vt = VEHICLE_TYPES.find(v => v.id === form.vehicleType) || VEHICLE_TYPES[0];
  const addOnList = addOnsFor(service);
  const basePrice = servicePrice(service, vt.id);
  const addOnsTotal = (form.addOns || []).reduce((s, id) => {
    const a = addOnList.find(x => x.id === id);
    return s + (a ? a.price : 0);
  }, 0);
  const autoPrice = basePrice + addOnsTotal + (form.rush ? RUSH_FEE : 0);
  const finalPrice = form.priceOverride !== '' && form.priceOverride !== undefined
    ? parseFloat(form.priceOverride) || 0
    : autoPrice;
  const depositAmount = form.paymentStatus === 'deposit'
    ? Math.round(finalPrice * 0.5)
    : form.paymentStatus === 'full' ? finalPrice : 0;

  // Validation per step
  const canAdvance = () => {
    if (step === 0) return !!(form.clientName.trim());
    if (step === 1) return !!(form.serviceId && form.vehicleType);
    if (step === 2) return !!(form.date && form.address.trim());
    return true;
  };

  const handleSubmit = async () => {
    setSaving(true);
    setError('');
    try {
      // 1. Upsert client if new
      let clientId = form.clientId;
      if (!clientId && form.clientName.trim()) {
        const { data: existing } = await supabase
          .from('clients')
          .select('id')
          .eq('email', form.email.trim().toLowerCase())
          .maybeSingle();

        if (existing) {
          clientId = existing.id;
          await supabase.from('clients').update({
            name: form.clientName.trim(),
            phone: form.phone.trim() || undefined,
            address: form.address.trim() || undefined,
          }).eq('id', clientId);
        } else {
          const { data: newClient, error: clientErr } = await supabase
            .from('clients')
            .insert({
              name: form.clientName.trim(),
              email: form.email.trim().toLowerCase() || null,
              phone: form.phone.trim() || null,
              address: form.address.trim() || null,
              year: form.year || null,
              make: form.make || null,
              model: form.model || null,
              color: form.color || null,
              type: service.id === 'maintenance_reset' ? 'maintenance' : 'one_time',
              status: 'active',
            })
            .select('id')
            .single();
          if (clientErr) throw new Error(clientErr.message);
          clientId = newClient.id;
        }
      }

      // 2. Build scheduled_at
      let scheduledAt = null;
      if (form.date) {
        const [h, m] = (form.time || '09:00').split(':').map(Number);
        const d = new Date(form.date + 'T12:00:00');
        d.setHours(h, m, 0, 0);
        scheduledAt = d.toISOString();
      }

      // 3. Add-on labels
      const addOnLabels = [
        ...(form.addOns || []).map(id => addOnList.find(x => x.id === id)?.label),
        form.rush ? `Rush / same-day fee (+$${RUSH_FEE})` : null,
      ].filter(Boolean).join(', ');

      // 4. Insert booking
      const { data: booking, error: bookingErr } = await supabase
        .from('bookings')
        .insert({
          client_id: clientId || null,
          client_name: form.clientName.trim(),
          email: form.email.trim() || null,
          phone: form.phone.trim() || null,
          booking_type: service.booking_type,
          service_type: service.label,
          tier: service.label,
          package_id: service.booking_type === 'one_time' ? service.id : null,
          price: finalPrice,
          deposit_amount: depositAmount,
          status: 'scheduled',
          address: form.address.trim() || null,
          year: form.year || null,
          make: form.make || null,
          model: form.model || null,
          color: form.color || null,
          add_ons: addOnLabels || null,
          scheduled_at: scheduledAt,
        })
        .select('id')
        .single();
      if (bookingErr) throw new Error(bookingErr.message);

      // 4b. Block the day on the public calendars (one car per day) so a
      // customer can't book over a manual/rush job. Best-effort, idempotent.
      if (form.date) {
        try {
          await supabase.from(service.booking_type === 'maintenance_reset' ? 'maintenance_reset_slots' : 'one_time_slots').upsert({
            id: `${form.date}_9am`,
            is_booked: true,
            booked_by: form.email.trim() || form.clientName.trim(),
            booked_at: new Date().toISOString(),
          });
        } catch {}
      }

      // 5. Activity log
      const activityNote = [
        `Manual booking created: ${form.clientName} — ${service.label} on ${form.date || 'TBD'}${form.rush ? ` (RUSH +$${RUSH_FEE})` : ''}.`,
        form.paymentStatus === 'deposit' ? `Deposit of $${depositAmount} collected.` : '',
        form.paymentStatus === 'full' ? `Full payment of $${finalPrice} collected.` : '',
        form.notes ? `Note: ${form.notes}` : '',
      ].filter(Boolean).join(' ');
      await supabase.from('activity_log').insert({
        note: activityNote,
        type: 'booking_created',
        client_id: clientId || null,
        booking_id: booking.id,
      });

      // 6. Send confirmation email/SMS if requested
      if (form.notifyEmail && form.email) {
        try {
          const { data: { session } } = await supabase.auth.getSession();
          await fetch('/api/send-confirmation', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${session?.access_token}`,
            },
            body: JSON.stringify({ bookingId: booking.id, sendEmail: true, sendSMS: false }),
          });
        } catch (confirmErr) {
          console.warn('Confirmation email failed (non-fatal):', confirmErr.message);
        }
      }

      navigate(`/admin/jobs/${booking.id}`);
    } catch (err) {
      setError(err.message || 'Failed to create booking.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="p-6 pb-24 md:pb-6 max-w-2xl mx-auto">
      <div className="flex items-center gap-3 mb-6">
        <Link to="/admin/bookings" className="text-gray-400 hover:text-gray-700">
          <ArrowLeft size={20} />
        </Link>
        <div>
          <h1 className="text-xl font-bold text-gray-900">New Booking</h1>
          <p className="text-sm text-gray-400">Create a booking manually — no Stripe required</p>
        </div>
      </div>

      <StepBar step={step} />

      <div className="bg-white rounded-2xl shadow-sm p-6 mb-6">
        {step === 0 && <StepClient data={form} onChange={update} />}
        {step === 1 && <StepService data={form} onChange={update} />}
        {step === 2 && <StepSchedule data={form} onChange={update} />}
        {step === 3 && <StepConfirm data={form} onChange={update} autoPrice={autoPrice} />}
      </div>

      {error && (
        <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-xl text-sm text-red-700 font-medium">
          {error}
        </div>
      )}

      <div className="flex items-center justify-between">
        <button
          onClick={() => setStep(s => Math.max(0, s - 1))}
          disabled={step === 0}
          className="flex items-center gap-2 px-4 py-2.5 border border-gray-200 rounded-xl text-sm font-medium text-gray-600 hover:bg-gray-50 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
        >
          <ArrowLeft size={15} /> Back
        </button>

        {step < STEPS.length - 1 ? (
          <button
            onClick={() => setStep(s => s + 1)}
            disabled={!canAdvance()}
            className="flex items-center gap-2 px-6 py-2.5 bg-[#C9A84C] text-black rounded-xl text-sm font-bold hover:bg-[#b8943d] disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
          >
            Next <ArrowRight size={15} />
          </button>
        ) : (
          <button
            onClick={handleSubmit}
            disabled={saving || !canAdvance()}
            className="flex items-center gap-2 px-6 py-2.5 bg-[#C9A84C] text-black rounded-xl text-sm font-bold hover:bg-[#b8943d] disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
          >
            {saving ? <><Loader2 size={15} className="animate-spin" /> Creating…</> : <><Check size={15} /> Create Booking</>}
          </button>
        )}
      </div>
    </div>
  );
}
